import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import GlobalError from '../components/GlobalError';
import { useSnackbar } from '../contexts/SnackbarProvider';
import ProductForm from '../components/ProductForm';
import useDataSelect from '../hooks/useDataSelect';
import axiosInstance from '../utils/axiosInstance';
import { prepareProductPayload } from '../utils/prepareProductPayload';
import { Typography, Button, Snackbar, Alert } from "@mui/material";
import { Sync, ContentCopy, Delete } from "@mui/icons-material";

const ProductDetailPage = ({ hasLimit }) => {
  const { showSnackbar } = useSnackbar();
  const location = useLocation();
  const [dataset, setDataset] = useState();
  const [imageFiles, setImageFiles] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState();
  const [variants, setVariants] = useState([]);
  const [optionTypes, setOptionTypes] = useState([]);
  const [specs, setSpecs] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const { id } = useParams();
  const navigate = useNavigate();
  const { register, handleSubmit, setValue, control, formState: { errors } } = useForm();
  const { dataset: productData, error, refetch } = useDataSelect(`/product/${id}`);
  useEffect(() => {
    if (location.state?.snackbar) {
      setSnackbar({ ...location.state.snackbar, open: true });
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate]);
  useEffect(() => {
    if (productData) {
      const updatedImageFiles = productData.images?.length
        ? productData.images.map(img => ({
          image_id: img.image_id,
          image_url: img.image_url,
          isExisting: true,
          willDelete: false
        }))
        : [null];

      const updatedVariants = productData.variants?.map(v => ({
        ...v,
        option_combination: v.option_combination || {},
        imageFiles: v.imageFiles?.map(img => ({
          image_id: img.image_id,
          image_url: img.image_url,
          image_order: img.image_order,
          image_type: img.image_type,
          isExisting: true,
          willDelete: false
        })) || []
      })) || [];

      const updatedSpecs = productData.specs?.map(spec => ({
        spec_key: spec.spec_key ?? '',
        spec_value: spec.spec_value ?? '',
        sort_order: spec.sort_order ?? 0,
      })) || [];

      setDataset(prev => ({
        ...prev,
        ...productData,
        images: updatedImageFiles,
      }));

      setImageFiles(updatedImageFiles);
      setSelectedCategory(productData.category_id || null);
      setVariants(updatedVariants);
      setSpecs(updatedSpecs);
    }
  }, [productData]);

  const handleCopy = async () => {
    if (!dataset) return;

    try {
      // 新規作成用にデータ調整
      const payloadDataset = {
        ...dataset,
        product_id: undefined,
        product_name: `${dataset.product_name}-copy`,
        product_code: `${dataset.product_code}-copy`, // 一意化
      };

      const payloadVariants = variants.map(v => ({
        ...v,
        variant_id: undefined,
        sku: `${v.sku}-copy`, // 一意化
      }));

      const payload = prepareProductPayload({
        data: payloadDataset,
        dataset: payloadDataset,
        imageFiles,
        variants: payloadVariants,
        specs
      });

      const isFormData = payload instanceof FormData;

      const res = await axiosInstance.post(
        '/product/new',
        payload,
        isFormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {}
      );
      navigate(`/products/${res.data.product_id}`, {
        state: {
          snackbar: { message: res?.data?.message || '商品が複製されました', severity: 'success' }
        }
      });
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error(err);
      }
      showSnackbar(err?.response?.data?.message || '商品複製に失敗しました', 'error');
    }
  };

  const onSubmit = async (data) => {
    try {
      const payload = prepareProductPayload({
        data,
        dataset,
        imageFiles,
        variants,
        specs,
      });

      // FormData かどうか判定
      const isFormData = payload instanceof FormData;
      const res = await axiosInstance.put(`/product/${dataset.product_id}`,
        payload,
        isFormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {}
      );
      showSnackbar(res?.data?.message || '商品情報が更新されました', 'success');
      setVariants([]);
      setOptionTypes([]);
      refetch();

    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error(err);
      }
      showSnackbar(err?.response?.data?.message || '商品情報の更新に失敗しました。', 'error');
    }
  };
  const handleDelete = async () => {
    if (window.confirm('本当に削除しますか？')) {
      try {
        const res = await axiosInstance.delete(`/product/${dataset.product_id}`);
        navigate('/products', {
          state: {
            snackbar: { message: res?.data?.message || '商品が削除されました', severity: 'success' }
          }
        });
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.error(err);
        }
        showSnackbar(err?.response?.data?.message || '商品削除に失敗しました', 'error');
      }
    }
  };

  if (error) return <GlobalError errorMessage={error} />;
  return (
    <>
      <Typography variant="h1" component="h1">商品情報</Typography>
      <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
        <ProductForm
          hasLimit={hasLimit}
          dataset={dataset}
          imageFiles={imageFiles} setImageFiles={setImageFiles}
          newImages={newImages} setNewImages={setNewImages}
          selectedCategory={selectedCategory} setSelectedCategory={setSelectedCategory}
          variants={variants} setVariants={setVariants}
          optionTypes={optionTypes} setOptionTypes={setOptionTypes}
          specs={specs} setSpecs={setSpecs}
          register={register} errors={errors} control={control} setValue={setValue}
        />
        <div className="button-row">
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={<Sync />}
          >
            更新する
          </Button>
          <Button
            type="button"
            variant="outlined"
            color="primary"
            startIcon={<ContentCopy />}
            onClick={handleCopy}
          >
            複製する
          </Button>

          <Button
            type="button"
            variant="outlined"
            color="error"
            startIcon={<Delete />}
            onClick={handleDelete}
          >
            削除する
          </Button>
        </div>
      </form>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ProductDetailPage;
